/**
 * Lab63 invisible captcha — no Google / third-party widgets, nothing for the user to click.
 * Fetches a server-signed challenge and silently finds a nonce so that sha256(`${salt}:${nonce}`)
 * has `difficulty` leading zero bits. Mirrors micus-mono/src/lib/invisible-captcha.ts.
 * Pure-JS SHA-256 so it also works outside secure contexts (crypto.subtle is https/localhost only).
 */

import api from '../services/api';

type ChallengeResponse = {
  success: boolean;
  token: string;
  salt: string;
  difficulty: number;
  issuedAt: number;
  expiresAt: number;
  minAgeMs: number;
};

export type InvisibleProof = { token: string; nonce: string };

export type SolvedChallenge = InvisibleProof & {
  /** Local clock when the challenge arrived — the server's issue time is never later than this. */
  receivedAt: number;
  minAgeMs: number;
  /** Local clock after which the token should be treated as expired (with a safety margin). */
  staleAt: number;
};

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
const W = new Uint32Array(64);

/** First 32 bits of SHA-256 over an ASCII string. */
export function sha256Head(input: string): number {
  const len = input.length;
  const size = ((len + 9 + 63) >> 6) << 6;
  const buf = new Uint8Array(size);
  for (let i = 0; i < len; i++) buf[i] = input.charCodeAt(i);
  buf[len] = 0x80;
  const bitLen = len * 8;
  buf[size - 4] = (bitLen >>> 24) & 0xff;
  buf[size - 3] = (bitLen >>> 16) & 0xff;
  buf[size - 2] = (bitLen >>> 8) & 0xff;
  buf[size - 1] = bitLen & 0xff;

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  for (let off = 0; off < size; off += 64) {
    for (let t = 0; t < 16; t++) {
      const j = off + t * 4;
      W[t] = (buf[j] << 24) | (buf[j + 1] << 16) | (buf[j + 2] << 8) | buf[j + 3];
    }
    for (let t = 16; t < 64; t++) {
      const w15 = W[t - 15];
      const w2 = W[t - 2];
      const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
      const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
      W[t] = (W[t - 16] + s0 + W[t - 7] + s1) | 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[t] + W[t]) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }
  return h0 >>> 0;
}

const CHUNK = 4000;
const STALE_MARGIN_MS = 60_000;

/** Finds the nonce in small chunks so typing stays smooth. Difficulty is capped server-side below 32 bits. */
export async function solvePow(salt: string, difficulty: number, signal?: AbortSignal): Promise<string> {
  const prefix = `${salt}:`;
  for (let nonce = 0; ; nonce += CHUNK) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    for (let n = nonce; n < nonce + CHUNK; n++) {
      if (Math.clz32(sha256Head(prefix + n)) >= difficulty) return String(n);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

export async function fetchAndSolve(signal?: AbortSignal): Promise<SolvedChallenge> {
  const { data } = await api.get<ChallengeResponse>('/captcha/invisible', { signal });
  const receivedAt = Date.now();
  const nonce = await solvePow(data.salt, data.difficulty, signal);
  return {
    token: data.token,
    nonce,
    receivedAt,
    minAgeMs: data.minAgeMs,
    staleAt: receivedAt + (data.expiresAt - data.issuedAt) - STALE_MARGIN_MS,
  };
}
